/**
 * Sizing engine. All physical quantities are kept at full precision in
 * canonical imperial units (ft, ft², ft³, CFM). Metric values are derived
 * only for display. Rounding happens at display time, never before a
 * qualification comparison.
 */

export type UnitSystem = 'imperial' | 'metric';

/** Particle concerns use particle CADR; `gases` is a separate pathway. */
export type Concern = 'general' | 'allergies' | 'pets' | 'dust' | 'wildfire' | 'gases';
export const PARTICLE_CONCERNS: Concern[] = ['general', 'allergies', 'pets', 'dust', 'wildfire'];

/** Branded numeric types so CFM and m³/h cannot be mixed silently. */
export type Cfm = number & { readonly __unit: 'cfm' };
export type M3h = number & { readonly __unit: 'm3h' };
export const cfm = (n: number) => n as Cfm;
export const m3h = (n: number) => n as M3h;

export const FT_PER_M = 3.280839895;
export const SQFT_PER_SQM = FT_PER_M * FT_PER_M; // 10.7639…
export const CUFT_PER_CUM = FT_PER_M ** 3; // 35.3147…
/** 1 CFM = 0.028316846592 m³/min × 60 = 1.69901 m³/h */
export const M3H_PER_CFM = 0.028316846592 * 60;

export const toM3h = (v: Cfm): M3h => m3h(v * M3H_PER_CFM);
export const toCfm = (v: M3h): Cfm => cfm(v / M3H_PER_CFM);
export const mToFt = (m: number) => m * FT_PER_M;
export const ftToM = (ft: number) => ft / FT_PER_M;
export const sqmToSqft = (m2: number) => m2 * SQFT_PER_SQM;
export const sqftToSqm = (ft2: number) => ft2 / SQFT_PER_SQM;

/** AHAM's room-size rating basis. Kept separate from this tool's default. */
export const AHAM_ROOM_RATING_EACH = 4.8;

export interface SizingTarget {
  id: 'general' | 'wildfire' | 'custom';
  each: number;
  label: string;
  basis: string;
  sourceUrl?: string;
}

export const GENERAL_TARGET: SizingTarget = {
  id: 'general',
  each: 5,
  label: '5 equivalent air changes per hour',
  basis:
    "This tool's default for everyday particle filtration. It sits just above AHAM's 4.8 ACH room-rating basis; it is a sizing choice, not a health threshold.",
  sourceUrl: 'https://ahamverifide.org/ach-faq/',
};

export const WILDFIRE_TARGET: SizingTarget = {
  id: 'wildfire',
  each: 7.5,
  label: '7.5 equivalent air changes per hour (smoke CADR)',
  basis:
    "AHAM's guidance for wildfire smoke: smoke CADR at least equal to floor area in ft² for an 8 ft ceiling. Scaled by room volume for other ceilings, that is 7.5 eACH. It is not a guaranteed safe exposure level.",
  sourceUrl: 'https://ahamverifide.org/ahams-air-filtration-standards/',
};

export const LIMITS = {
  areaSqFt: { min: 10, max: 20000 },
  ceilingFt: { min: 6, max: 30 },
  each: { min: 1, max: 20 },
} as const;

export interface Room {
  /** Floor area in ft², full precision */
  areaSqFt: number;
  /** Ceiling height in ft, full precision */
  ceilingFt: number;
}

export const roomVolumeFt3 = (r: Room) => r.areaSqFt * r.ceilingFt;
export const roomVolumeM3 = (r: Room) => roomVolumeFt3(r) / CUFT_PER_CUM;

/** Required CADR in CFM = volume(ft³) × eACH ÷ 60 */
export function requiredCfm(r: Room, each: number): Cfm {
  return cfm((roomVolumeFt3(r) * each) / 60);
}

/** Required CADR in m³/h = volume(m³) × eACH (identical physical quantity) */
export function requiredM3h(r: Room, each: number): M3h {
  return m3h(roomVolumeM3(r) * each);
}

/** Achieved equivalent ACH = total CADR × 60 ÷ volume(ft³) */
export function achievedEach(totalCfm: number, r: Room): number {
  return (totalCfm * 60) / roomVolumeFt3(r);
}

/** Choose the sizing target. Wildfire takes precedence over general particle concerns. */
export function chooseTarget(concerns: Concern[], customEach?: number | null): SizingTarget | null {
  const particle = concerns.filter((c) => c !== 'gases');
  if (concerns.length > 0 && particle.length === 0) return null; // gas-only: no particle target
  if (customEach != null && isFinite(customEach)) {
    return {
      id: 'custom',
      each: customEach,
      label: `${customEach} equivalent air changes per hour (your setting)`,
      basis: 'A target you entered. It replaces the default and the wildfire preset.',
    };
  }
  return particle.includes('wildfire') ? WILDFIRE_TARGET : GENERAL_TARGET;
}

export interface ValidationResult {
  ok: boolean;
  errors: Partial<Record<'area' | 'ceiling' | 'each', string>>;
}

export function validateRoom(r: Partial<Room>, each?: number | null): ValidationResult {
  const errors: ValidationResult['errors'] = {};
  const a = r.areaSqFt;
  const h = r.ceilingFt;
  if (a == null || !isFinite(a)) errors.area = 'Enter the floor area or both room dimensions.';
  else if (a < LIMITS.areaSqFt.min || a > LIMITS.areaSqFt.max)
    errors.area = `Floor area must be between ${LIMITS.areaSqFt.min} and ${LIMITS.areaSqFt.max.toLocaleString()} ft² (about 1–1,858 m²).`;
  if (h == null || !isFinite(h)) errors.ceiling = 'Enter a ceiling height.';
  else if (h < LIMITS.ceilingFt.min || h > LIMITS.ceilingFt.max)
    errors.ceiling = `Ceiling height must be between ${LIMITS.ceilingFt.min} and ${LIMITS.ceilingFt.max} ft (about 1.8–9.1 m).`;
  if (each != null && (!isFinite(each) || each < LIMITS.each.min || each > LIMITS.each.max))
    errors.each = `Air changes per hour must be between ${LIMITS.each.min} and ${LIMITS.each.max}.`;
  return { ok: Object.keys(errors).length === 0, errors };
}

/** Parse a user-typed number strictly. Returns null for empty, NaN, Infinity or negatives. */
export function parsePositive(text: string | null | undefined): number | null {
  if (text == null) return null;
  const t = String(text).trim().replace(',', '.');
  if (!/^\d*\.?\d+$/.test(t)) return null;
  const n = Number(t);
  return isFinite(n) && n > 0 ? n : null;
}

// ---------- display helpers ----------
export const fmt = (n: number, dp = 0) =>
  n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: dp });
/** Required CADR is rounded UP for display so a displayed target is never below the real one. */
export const fmtRequiredCfm = (v: Cfm) => `${fmt(Math.ceil(v - 1e-9))} CFM`;
export const fmtRequiredM3h = (v: M3h) => `${fmt(Math.ceil(v - 1e-9))} m³/h`;
export function fmtArea(r: Room, u: UnitSystem) {
  return u === 'metric' ? `${fmt(sqftToSqm(r.areaSqFt), 1)} m²` : `${fmt(r.areaSqFt, 1)} ft²`;
}
export function fmtCeiling(r: Room, u: UnitSystem) {
  return u === 'metric' ? `${fmt(ftToM(r.ceilingFt), 2)} m` : `${fmt(r.ceilingFt, 2)} ft`;
}

// ---------- concerns ----------
export const CONCERN_LABELS: Record<Concern, string> = {
  general: 'General airborne particles',
  allergies: 'Pollen / allergy particles',
  pets: 'Pet dander (airborne)',
  dust: 'Airborne dust',
  wildfire: 'Wildfire smoke particles',
  gases: 'Odours, gases or VOCs',
};

export function formatConcerns(cs: Concern[]): string {
  if (cs.length === 0) return CONCERN_LABELS.general;
  return cs.map((c) => CONCERN_LABELS[c]).join(', ');
}

// ---------- presets (canonical feet) ----------
export interface RoomPreset {
  id: string;
  label: string;
  lengthFt: number;
  widthFt: number;
  ceilingFt: number;
}
export const ROOM_PRESETS: RoomPreset[] = [
  { id: 'small-bedroom', label: 'Small bedroom (10×10 ft)', lengthFt: 10, widthFt: 10, ceilingFt: 8 },
  { id: 'bedroom', label: 'Bedroom (12×12.5 ft)', lengthFt: 12, widthFt: 12.5, ceilingFt: 8 },
  { id: 'living', label: 'Living room (15×20 ft)', lengthFt: 15, widthFt: 20, ceilingFt: 8 },
  { id: 'open-plan', label: 'Open plan (20×25 ft)', lengthFt: 20, widthFt: 25, ceilingFt: 9 },
];
